import { useEffect, useMemo, useState } from "react";
import { Printer, Save, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";
import DietaryMark from "@/components/DietaryMark";

// Party items may be strings (legacy) or {name, dietary}
const itemName = (it) => (typeof it === "string" ? it : it?.name);
const itemDiet = (it) => (typeof it === "string" ? "" : (it?.dietary || ""));

export default function KitchenBrief() {
  const [packages, setPackages] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [mode, setMode] = useState("package");
  const [pkgId, setPkgId] = useState("");
  const [eventName, setEventName] = useState("");
  const [pax, setPax] = useState(30);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:30");
  const [host, setHost] = useState("");
  const [notes, setNotes] = useState("");
  const [customPicks, setCustomPicks] = useState({});
  const [customSource, setCustomSource] = useState("party"); // "party" | "alacarte"
  const [presets, setPresets] = useState([]);
  const [presetOpen, setPresetOpen] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [kot, setKot] = useState(null); // { number, label }

  const loadPresets = () => {
    api.get("/staff/brief-presets").then((r) => setPresets(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/party-packages").then((r) => {
      setPackages(r.data);
      if (r.data.length > 0) setPkgId(r.data[0].id);
    }).catch(() => {});
    api.get("/menu-items").then((r) => setMenuItems(r.data)).catch(() => {});
    loadPresets();
  }, []);

  const newKot = async () => {
    try {
      const { data } = await api.post("/staff/kot/next");
      setKot(data);
      toast.success(`New order number: ${data.label}`);
    } catch { toast.error("Could not fetch new KOT"); }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    const picks = Object.values(customPicks);
    if (picks.length === 0) { toast.error("Pick at least one dish first"); return; }
    try {
      await api.post("/staff/brief-presets", { name, picks });
      toast.success(`Saved "${name}"`);
      setSavingPreset(false);
      setPresetName("");
      loadPresets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const loadPreset = (p) => {
    const next = {};
    p.picks.forEach((pick) => {
      next[`${pick.section}::${pick.name}`] = { section: pick.section, name: pick.name, dietary: pick.dietary || "" };
    });
    setCustomPicks(next);
    setMode("custom");
    setPresetOpen(false);
    toast.success(`Loaded "${p.name}" (${p.picks.length} dishes)`);
  };

  const deletePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/staff/brief-presets/${p.id}`);
      toast.success("Deleted");
      loadPresets();
    } catch { toast.error("Delete failed"); }
  };

  const pkg = useMemo(() => packages.find((p) => p.id === pkgId), [packages, pkgId]);

  const togglePick = (section, item) => {
    setCustomPicks((prev) => {
      const key = `${section}::${itemName(item)}`;
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { section, name: itemName(item), dietary: itemDiet(item), qty: 1, price: item?.price || "" };
      return next;
    });
  };

  const setQty = (key, delta) => setCustomPicks((prev) => {
    if (!prev[key]) return prev;
    const nextQty = Math.max(1, (prev[key].qty || 1) + delta);
    return { ...prev, [key]: { ...prev[key], qty: nextQty } };
  });

  // Parse an à-la-carte price string ("₹250", "250", "250/piece") into a number for totals.
  const parsePrice = (s) => {
    if (!s) return 0;
    const m = String(s).replace(/[^\d.]/g, "");
    const n = parseFloat(m);
    return Number.isFinite(n) ? n : 0;
  };

  const alacarteTotal = useMemo(() => (
    Object.values(customPicks).reduce((sum, p) => sum + parsePrice(p.price) * (p.qty || 1), 0)
  ), [customPicks]);

  const hasAnyPricedPick = useMemo(() => (
    Object.values(customPicks).some((p) => parsePrice(p.price) > 0)
  ), [customPicks]);

  const customSections = useMemo(() => {
    const grouped = {};
    Object.values(customPicks).forEach(({ section, name, dietary, qty, price }) => {
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push({ name, dietary, qty: qty || 1, price: price || "" });
    });
    return Object.entries(grouped).map(([name, items]) => ({ name, items }));
  }, [customPicks]);

  const alacarteSections = useMemo(() => {
    const grouped = {};
    (menuItems || []).filter((m) => m.active !== false).forEach((m) => {
      const sec = m.section || "Other";
      if (!grouped[sec]) grouped[sec] = [];
      grouped[sec].push({ name: m.name, dietary: m.dietary || "", price: m.price || "" });
    });
    return Object.entries(grouped).map(([name, items]) => ({ name, items }));
  }, [menuItems]);

  const briefSections = mode === "package" ? (pkg?.sections || []) : customSections;
  const briefTitle = mode === "package" && pkg ? `${pkg.tagline} — ₹${pkg.price} package` : "Custom Selection";

  return (
    <div data-testid="staff-kitchen-brief">
      <div className="print:hidden">
        <div className="eyebrow">Kitchen Brief</div>
        <h1 className="mt-2 text-4xl">Print a briefing sheet</h1>
        <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
          Choose a preset package, or hand-pick individual dishes from both the party menu and the
          à-la-carte menu. Enter the pax, date, and time — then hit print for a clean B&amp;W sheet
          the kitchen can pin up.
        </p>

        <div className="mt-6 inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]">
          {[["package", "Package preset"], ["custom", "Custom mix"]].map(([v, l]) => (
            <button key={v} data-testid={`brief-mode-${v}`} onClick={() => setMode(v)}
              className={`px-5 py-2 text-sm rounded-full transition-all ${mode === v ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
            >{l}</button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Event / Host name"><input className="kb-input" data-testid="brief-event-name" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g., Sharma anniversary" /></F>
          <F label="Booked under"><input className="kb-input" data-testid="brief-host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="Contact person" /></F>
          <F label="Date"><input className="kb-input" type="date" data-testid="brief-date" value={date} onChange={(e) => setDate(e.target.value)} /></F>
          <F label="Service time"><input className="kb-input" type="time" data-testid="brief-time" value={time} onChange={(e) => setTime(e.target.value)} /></F>
          <F label="Pax"><input className="kb-input" type="number" min="1" max="500" data-testid="brief-pax" value={pax} onChange={(e) => setPax(Number(e.target.value))} /></F>
          {mode === "package" && (
            <F label="Package">
              <select className="kb-input" data-testid="brief-package" value={pkgId} onChange={(e) => setPkgId(e.target.value)}>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>{`₹${p.price} — ${p.tagline}`}</option>
                ))}
              </select>
            </F>
          )}
          <F label="Kitchen notes (allergies, timing, VIPs)" full>
            <textarea className="kb-input resize-none" rows={2} data-testid="brief-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </F>
        </div>

        {mode === "custom" && (
          <div className="mt-8" data-testid="brief-custom-picker">
            {/* Presets bar */}
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <div className="relative">
                <button
                  type="button"
                  data-testid="preset-load-btn"
                  onClick={() => { loadPresets(); setPresetOpen((v) => !v); }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--rule))] rounded bg-[hsl(var(--bg-2))] hover:border-[hsl(var(--brass))]"
                >
                  Load preset ({presets.length}) <ChevronDown size={14} />
                </button>
                {presetOpen && (
                  <div data-testid="preset-list" className="absolute left-0 top-full mt-1 z-10 w-72 max-h-80 overflow-auto border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] shadow-lg">
                    {presets.length === 0 && <div className="p-4 text-xs text-[hsl(var(--ink-soft))] italic">No presets yet. Save your first mix below.</div>}
                    {presets.map((p) => (
                      <div key={p.id} data-testid={`preset-row-${p.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-[hsl(var(--bg-2))] text-sm border-b border-[hsl(var(--rule))] last:border-0">
                        <button className="flex-1 text-left" onClick={() => loadPreset(p)} data-testid={`preset-load-${p.id}`}>
                          <div>{p.name}</div>
                          <div className="text-[10px] text-[hsl(var(--ink-soft))]">{p.picks.length} dishes</div>
                        </button>
                        <button onClick={() => deletePreset(p)} data-testid={`preset-delete-${p.id}`} className="text-[hsl(var(--ink-soft))] hover:text-red-600" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                data-testid="preset-save-toggle"
                onClick={() => setSavingPreset((v) => !v)}
                disabled={Object.keys(customPicks).length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--brass))] rounded bg-[hsl(var(--bg-2))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={14} /> Save as preset
              </button>
            </div>
            {savingPreset && (
              <div data-testid="preset-save-form" className="mb-4 flex items-center gap-2 p-3 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] rounded">
                <input
                  data-testid="preset-name-input"
                  autoFocus
                  placeholder="Preset name (e.g., Diwali family mix)"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && savePreset()}
                  className="flex-1 kb-input"
                />
                <button data-testid="preset-save-btn" onClick={savePreset} className="btn-brass !py-2 !px-4 text-sm">Save</button>
                <button onClick={() => { setSavingPreset(false); setPresetName(""); }} className="text-sm text-[hsl(var(--ink-soft))] px-2">Cancel</button>
              </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="eyebrow">Pick dishes from party menu or à la carte</div>
              <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]">
                {[["party", `Party menu (${packages.length})`], ["alacarte", `À la carte (${menuItems.filter((m) => m.active !== false).length})`]].map(([v, l]) => (
                  <button key={v} data-testid={`brief-source-${v}`} onClick={() => setCustomSource(v)}
                    className={`px-4 py-1.5 text-xs rounded-full transition-all ${customSource === v ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
                  >{l}</button>
                ))}
              </div>
            </div>

            {customSource === "party" && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((p) => (
                <div key={p.id} className="border border-[hsl(var(--rule))] p-5 bg-[hsl(var(--bg-2))]">
                  <div className="text-sm font-semibold">₹{p.price} · {p.tagline}</div>
                  <div className="rule-brass my-3" />
                  {p.sections.map((s) => (
                    <div key={s.name} className="mb-3">
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--brass-2))] font-semibold">{s.name}</div>
                      <ul className="mt-1 space-y-1">
                        {s.items.map((it) => {
                          const n = itemName(it);
                          const key = `${s.name}::${n}`;
                          const checked = !!customPicks[key];
                          return (
                            <li key={n}>
                              <label className="flex items-start gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={checked} onChange={() => togglePick(s.name, it)} className="mt-1 accent-[hsl(var(--forest))]" />
                                <DietaryMark value={itemDiet(it)} className="translate-y-[3px]" />
                                <span>{n}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            )}

            {customSource === "alacarte" && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="brief-alacarte-panel">
              {alacarteSections.map((s) => (
                <div key={s.name} className="border border-[hsl(var(--rule))] p-5 bg-[hsl(var(--bg-2))]">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--brass-2))] font-semibold">{s.name}</div>
                  <div className="rule-brass my-3" />
                  <ul className="space-y-1.5">
                    {s.items.map((it) => {
                      const key = `${s.name}::${it.name}`;
                      const checked = !!customPicks[key];
                      return (
                        <li key={it.name}>
                          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`brief-alacarte-item-${it.name.replace(/\s+/g, "-").toLowerCase()}`}>
                            <input type="checkbox" checked={checked} onChange={() => togglePick(s.name, { name: it.name, dietary: it.dietary, price: it.price })} className="mt-1 accent-[hsl(var(--forest))]" />
                            <DietaryMark value={it.dietary} className="translate-y-[3px]" />
                            <span className="flex-1">
                              {it.name}
                              {it.price && <span className="ml-1.5 text-[hsl(var(--ink-soft))] text-xs">{it.price}</span>}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {alacarteSections.length === 0 && (
                <div className="text-sm text-[hsl(var(--ink-soft))] italic">No à-la-carte dishes yet. Add some in Menu editor.</div>
              )}
            </div>
            )}

            {Object.keys(customPicks).length > 0 && (
              <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4" data-testid="brief-picks-summary">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="eyebrow">Your picks</div>
                  <button data-testid="brief-clear-picks" onClick={() => setCustomPicks({})} className="text-xs text-[hsl(var(--brass-2))] hover:underline">Clear all</button>
                </div>
                <ul className="mt-3 divide-y divide-[hsl(var(--rule))]">
                  {Object.entries(customPicks).map(([key, p]) => {
                    const unit = parsePrice(p.price);
                    const line = unit * (p.qty || 1);
                    return (
                      <li key={key} data-testid={`pick-row-${p.name}`} className="py-2 flex items-center gap-3 text-sm">
                        <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))] w-28 shrink-0">{p.section}</span>
                        <span className="flex-1">{p.name}{p.price && <span className="ml-2 text-xs text-[hsl(var(--ink-soft))]">{p.price}</span>}</span>
                        <div className="inline-flex items-center border border-[hsl(var(--rule))] rounded overflow-hidden">
                          <button type="button" onClick={() => setQty(key, -1)} data-testid={`qty-dec-${p.name}`} className="px-2 py-0.5 hover:bg-[hsl(var(--bg))]">−</button>
                          <span className="px-3 py-0.5 min-w-[2rem] text-center font-semibold" data-testid={`qty-val-${p.name}`}>{p.qty || 1}</span>
                          <button type="button" onClick={() => setQty(key, +1)} data-testid={`qty-inc-${p.name}`} className="px-2 py-0.5 hover:bg-[hsl(var(--bg))]">+</button>
                        </div>
                        <span className="w-24 text-right text-[hsl(var(--forest))] font-mono">
                          {line > 0 ? `₹${line.toFixed(0)}` : "—"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {hasAnyPricedPick && (
                  <div className="mt-3 pt-3 border-t border-[hsl(var(--rule))] flex items-center justify-between text-sm">
                    <span className="text-[hsl(var(--ink-soft))]">Approx. total (à-la-carte only)</span>
                    <span data-testid="brief-alacarte-total" className="text-lg font-mono font-semibold text-[hsl(var(--forest))]">₹{alacarteTotal.toFixed(0)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-8 flex gap-3 flex-wrap items-center">
          <button data-testid="brief-print-btn" onClick={async () => {
            try {
              if (kot?.label) {
                // Build dish list from either the picked custom items or the selected package.
                const dishes = mode === "custom"
                  ? Object.values(customPicks).map((p) => ({
                      section: p.section, name: p.name, dietary: p.dietary || "",
                      qty: p.qty || 1, price: p.price || "",
                    }))
                  : (pkg?.sections || []).flatMap((s) => (s.items || []).map((it) => ({
                      section: s.name, name: itemName(it), dietary: itemDiet(it), qty: 1, price: "",
                    })));
                await api.post("/staff/briefs/log", {
                  kot_label: kot.label,
                  event_name: eventName,
                  host,
                  date,
                  time,
                  pax: Number(pax) || 0,
                  mode,
                  package_tagline: mode === "package" && pkg ? `₹${pkg.price} · ${pkg.tagline}` : "",
                  dish_count: dishes.length,
                  dishes,
                  approx_total: mode === "custom" ? Math.round(alacarteTotal) : 0,
                  notes: notes || "",
                });
              }
            } catch (err) { console.error("Brief log failed (non-blocking):", err); }
            window.print();
          }} className="btn-brass">
            <Printer size={16} /> Print briefing
          </button>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[hsl(var(--ink-soft))]">Order #</span>
            {kot ? (
              <>
                <span data-testid="brief-kot-label" className="font-mono font-semibold text-[hsl(var(--forest))] px-3 py-1.5 bg-[hsl(var(--bg-2))] border border-[hsl(var(--rule))] rounded">
                  {kot.label}
                </span>
                <button type="button" data-testid="brief-kot-new" onClick={newKot} className="text-xs text-[hsl(var(--brass-2))] hover:underline">New number</button>
              </>
            ) : (
              <button type="button" data-testid="brief-kot-generate" onClick={newKot} className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--brass))] rounded bg-[hsl(var(--bg-2))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors">
                Generate KOT #
              </button>
            )}
          </div>
        </div>
      </div>

      <section data-testid="brief-print-sheet" className="mt-10 print:mt-0 print:p-0 bg-white text-black print-sheet">
        <div className="border-b-2 border-black pb-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em]">The Barracks Bistro · Kitchen Brief</div>
            <div className="text-3xl font-serif mt-1">{briefTitle}</div>
            {kot && (
              <div data-testid="brief-kot-print" className="mt-2 inline-block px-3 py-1 border-2 border-black text-sm font-mono font-bold tracking-wider">
                {kot.label}
              </div>
            )}
          </div>
          <div className="text-right text-xs">
            <div>{SITE.address}</div>
            <div>{SITE.phonePrimary} · {SITE.email}</div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-4 text-sm">
          <Meta k="Event" v={eventName || "—"} />
          <Meta k="Host" v={host || "—"} />
          <Meta k="Date" v={date || "—"} />
          <Meta k="Service time" v={time || "—"} />
          <Meta k="Pax" v={String(pax)} />
          {mode === "package" && pkg && <Meta k="Package" v={`₹${pkg.price} · ${pkg.tagline}`} />}
          {mode === "package" && pkg && <Meta k="Per-pax cost" v={`₹${pkg.price}`} />}
          {mode === "package" && pkg && <Meta k="Est. revenue" v={`₹${(pkg.price * pax).toLocaleString("en-IN")}`} />}
        </div>

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold border-b border-black pb-1">Menu</div>
          {briefSections.length === 0 && (<div className="mt-6 text-sm italic">No dishes selected.</div>)}
          <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4">
            {briefSections.map((s) => (
              <div key={s.name} className="break-inside-avoid">
                <div className="text-[11px] uppercase tracking-widest font-semibold">{s.name}</div>
                <ul className="mt-1.5 text-sm leading-6">
                  {s.items.map((it) => {
                    const n = itemName(it);
                    const d = itemDiet(it);
                    const marker = d === "veg" ? "●" : d === "non-veg" ? "■" : "·";
                    const qty = typeof it === "object" ? it?.qty : null;
                    const unit = typeof it === "object" ? parsePrice(it?.price) : 0;
                    const line = unit * (qty || 1);
                    return (
                      <li key={n} className="flex justify-between gap-4">
                        <span>{marker} {n}{qty && qty > 1 ? <span className="ml-1 font-semibold">× {qty}</span> : null}</span>
                        {line > 0 && <span className="font-mono text-xs shrink-0">₹{line.toFixed(0)}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          {mode === "custom" && hasAnyPricedPick && (
            <div className="mt-4 pt-3 border-t border-black flex items-center justify-between text-sm">
              <span className="uppercase tracking-widest text-[11px] font-semibold">Approx. total (à-la-carte)</span>
              <span className="font-mono font-bold">₹{alacarteTotal.toFixed(0)}</span>
            </div>
          )}
        </div>

        {notes && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold border-b border-black pb-1">Kitchen notes</div>
            <p className="mt-2 text-sm whitespace-pre-wrap">{notes}</p>
          </div>
        )}

        <div className="mt-8 pt-4 border-t border-black grid grid-cols-3 text-[10px] uppercase tracking-widest">
          <div>Head chef: ______________</div>
          <div>Service lead: ____________</div>
          <div>Printed: {new Date().toLocaleString("en-IN")}</div>
        </div>
      </section>

      <style>{`
        .kb-input {
          width: 100%; background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule)); border-radius: 4px;
          padding: 0.65rem 0.9rem; font-family: inherit;
          font-size: 0.9rem; color: hsl(var(--ink));
        }
        .kb-input:focus { outline: none; border-color: hsl(var(--forest)); }
        .print-sheet { padding: 2rem; border: 1px solid hsl(var(--rule)); }
        @media print {
          body { background: white !important; }
          .print-sheet { border: none; padding: 0; font-family: Georgia, serif; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] mb-2">{label}</span>
      {children}
    </label>
  );
}

function Meta({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest">{k}</div>
      <div className="text-sm font-semibold">{v}</div>
    </div>
  );
}
